import React, {useRef, useState} from "react";
import {CompletePayroll, Employee} from "../../../../app/interfaces";
import {PayrollPreparingContainer} from "./styles";
import {useTranslation} from "react-i18next";
import {hasBonus, isPositiveNumber, isValidString} from "../../../../utils";
import {
    InputFieldsWrapper,
    InputText,
    PopupAskForConfirmation,
    PopupLoading,
    PrimaryButton
} from "../../../../components";
import {FETCH_STATUS_LOADING} from "../../../../utils/constants";
import {useDispatch, useSelector} from "react-redux";
import {addEorInvoiceDetails} from "../../../../app/payroll/actions";
import {updatePayrollState} from "../../../../app/payroll/selectors";
import MultiUploadArea from "../../../../components/MultiUploadArea";

interface formFieldProps {
    gross_salary: string | number;
    eor_fee: string | number | any;
    social_taxes: string | number | any;
    setup_fee?: string | number;
    security_deposit?: string;
    bonus?: string | number;
}

interface formErrors {
    eor_fee: boolean;
    social_taxes: boolean;
    setup_fee: boolean;
    eor_partner_invoice: boolean;
    security_deposit: boolean;
    gross_salary: boolean;
    bonus: boolean;
}
const PayrollPreparing = ({payroll}: {payroll: CompletePayroll}) => {

    const employee: Employee = payroll.eor_employee;

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [formFields, setFormFields] = useState<formFieldProps>({
        eor_fee: employee?.quote?.eor_fee,
        social_taxes: employee?.quote?.social_taxes,
        gross_salary: employee.salary.amount || '',
        ...(payroll.is_first_payroll ? {
            security_deposit : '',
            setup_fee : ''
        } : null),
        ...(hasBonus(payroll) ? {
            bonus: payroll.bonus.amount
        } : null)
    })

    const [formErrors, setFormErrors] = useState<formErrors>({
        eor_fee: false,
        social_taxes: false,
        setup_fee: false,
        eor_partner_invoice: false,
        security_deposit: false,
        gross_salary: false,
        bonus: false
    })
    const [files, setFiles] = useState<{base64: string, file_name: string}[]>([])

    const [showAskForConfirmation, setShowAskForConfirmation] = useState<boolean>(false)

    const dispatch = useDispatch()

    const {t} = useTranslation()

    const statusUpdate = useSelector(updatePayrollState)

    const handleInputChange = (e: React.SyntheticEvent) => {
        const key = (e.target as HTMLElement).id || (e.target as HTMLInputElement).name;
        const {value} = e.target as HTMLInputElement;

        const isPositive = isPositiveNumber(value)
        setFormErrors((prevState) => ({
            ...prevState,
            [key]: !isPositive,
        }));
        setFormFields((prevState) => {
            return {
                ...prevState,
                [key]: value
            }
        })
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files && e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setFormFields((prevState) => ({
                    ...prevState,
                    eor_partner_invoice: {
                        base64: reader.result as string,
                        file_name: file.name
                    }
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleRemoveInvoiceFile = () => {
        setFormFields((prevState) => {
            return {
                ...prevState,
                eor_partner_invoice: {
                    base64: '',
                    file_name: ''
                },
            }
        })
    }

    const handleSubmitInvoiceInfo = async () => {
        setShowAskForConfirmation(false)
        await dispatch(addEorInvoiceDetails({
            ...formFields,
            eor_partner_invoices: files,
            is_first_payroll: payroll.is_first_payroll,
            payrollId: payroll._id,
            employeeId: employee._id,
            ...(formFields.hasOwnProperty('bonus') ? {
                bonus: {
                    ...payroll.bonus,
                    amount: formFields.bonus
                }
            } : null)
        }) as any)
    }

    const noErrors = (): boolean => Object.values(formErrors).every((value) => value === false);

    const areFieldsValid = () => {
        let fields = noErrors() &&
            isPositiveNumber(formFields.eor_fee) &&
            isPositiveNumber(formFields.social_taxes) &&
            isPositiveNumber(formFields.gross_salary) &&
            Array.isArray(files) &&
            files.length >= 1 &&
            files.every((file) => file.base64 && isValidString(file.base64) && file.file_name && isValidString(file.file_name))

        let firstPaymentFields = true;
        if(payroll.is_first_payroll){
            firstPaymentFields =  isPositiveNumber(formFields.setup_fee) &&
                isPositiveNumber(formFields.security_deposit)
        }
        return fields && firstPaymentFields;
    }

    if(statusUpdate === FETCH_STATUS_LOADING){
        return <PopupLoading isOpen={true}/>
    }

    return <PayrollPreparingContainer>

        {showAskForConfirmation && <PopupAskForConfirmation
            title={t('wait_a_minute') as string}
            subtitle={t('charge_customer_alert') as string}
            isOpen={showAskForConfirmation}
            onConfirm={handleSubmitInvoiceInfo}
            onCancel={() => setShowAskForConfirmation(false)}
            type='hand_stop'
        />}

        <h1>{t('add_payroll_costs')}</h1>
        {/*Bonus information is applicable*/}
        {hasBonus(payroll) && <section>
            <h3>{t('bonus_information')}</h3>
            <InputFieldsWrapper>
                <InputText
                    label={t('bonus') as string}
                    value={formFields.bonus}
                    onChange={(e) => handleInputChange(e)}
                    id='bonus'
                    status={formErrors.bonus ? 'danger' : 'warning'}
                    helperText={formErrors.bonus ? t('enter_a_valid_number') as string : t('requested_bonus_of', {amount: payroll.bonus.amount}) as string}
                />
                <InputText
                    readOnly
                    label={t('currency') as string}
                    value={payroll.bonus.currency}>
                </InputText>
            </InputFieldsWrapper>
            <InputText
                readOnly
                label={t('description') as string}
                value={payroll.bonus.description || ''}>
            </InputText>
        </section>}

        {/**/}
        <section>
            <h3>{t('payroll_costs')}</h3>
            <InputFieldsWrapper>
                <InputText
                    label={t('gross_salary') as string}
                    value={formFields.gross_salary}
                    onChange={(e) => handleInputChange(e)}
                    id='gross_salary'
                    status={formErrors.gross_salary ? 'danger' : 'normal'}
                    helperText={formErrors.gross_salary ? t('enter_a_valid_number') as string : ''}
                />
                <InputText
                    readOnly
                    label={t('currency') as string}
                    value={payroll.eor_employee.salary.currency}>
                </InputText>
            </InputFieldsWrapper>
            <InputFieldsWrapper>
                <InputText
                    label={t('eor_fee') as string}
                    value={formFields.eor_fee}
                    onChange={(e) => handleInputChange(e)}
                    id='eor_fee'
                    status={formErrors.eor_fee ? 'danger' : 'normal'}
                    helperText={formErrors.eor_fee ? t('enter_a_valid_number') as string : ''}
                />
                <InputText
                    label={t('social_taxes') as string}
                    value={formFields.social_taxes}
                    onChange={(e) => handleInputChange(e)}
                    id='social_taxes'
                    status={formErrors.social_taxes ? 'danger' : 'normal'}
                    helperText={formErrors.social_taxes ? t('enter_a_valid_number') as string : ''}
                />
            </InputFieldsWrapper>
            {payroll.is_first_payroll && <InputFieldsWrapper>
                <InputText
                    label={t('setup_fee') as string}
                    value={formFields.setup_fee}
                    onChange={(e) => handleInputChange(e)}
                    id='setup_fee'
                    status={formErrors.setup_fee ? 'danger' : 'normal'}
                    helperText={formErrors.setup_fee ? t('enter_a_valid_number') as string : ''}
                />
                <InputText
                    label={t('security_deposit') as string}
                    value={formFields.security_deposit}
                    onChange={(e) => handleInputChange(e)}
                    id='security_deposit'
                    status={formErrors.security_deposit ? 'danger' : 'normal'}
                    helperText={formErrors.security_deposit ? t('enter_a_valid_number') as string : ''}
                />
            </InputFieldsWrapper>}
        </section>
        <section>
            <h3>{t('eor_partner_invoice')}</h3>
            <MultiUploadArea
                files={files}
                setFiles={setFiles}
                accept={{
                    'application/pdf': ['.pdf'],
                    'application/msword': ['.doc'],
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                    'image/jpeg': ['.jpeg', '.jpg'],
                    'image/png': ['.png'],
                }}
            />
            {/*<input type='file' accept='.pdf,.doc,.docx,.jpeg,.jpg,.png' onChange={handleFileInputChange}
                   ref={fileInputRef}/>
            {formFields.eor_partner_invoice.base64 && formFields.eor_partner_invoice.file_name ?
                <PreviewEorInvoice>
                    <button
                        onClick={() => {downloadBase64File(
                            formFields.eor_partner_invoice.base64,
                            formFields.eor_partner_invoice.file_name
                        )}}>
                        {formFields.eor_partner_invoice.file_name}
                    </button>
                    <CloseRounded onClick={handleRemoveInvoiceFile}/>
                </PreviewEorInvoice> :
                <UploadInvoice onClick={handleUploadClick}>
                    <div className="icon"><UploadClip/> </div>
                    <p>{t('upload_document')}</p>
                </UploadInvoice>}*/}
        </section>
        <div className="submit">
            <PrimaryButton disabled={!areFieldsValid()} onClick={() => setShowAskForConfirmation(true)}>{t('submit')}</PrimaryButton>
        </div>

    </PayrollPreparingContainer>
}

export default PayrollPreparing;
